<template>
  <div>
    <!-- 正常内容 -->
    <div>
      <div class="message-container" :class="{ 'user-message': isUser }">
        <!-- 只有AI消息显示头像 -->
        <div class="avatar" v-if="!isUser">
          <img src="@/assets/image/avatar.png" alt="AI头像" class="avatar-img">
        </div>
        <!-- 用户消息占位区，不显示头像 -->
        <div class="empty-avatar-space" v-if="isUser"></div>

        <div class="message-content">
          <div class="message-body" v-html="displayedContent"/>
          <!-- 条件渲染：如果有diseaseData则显示病害识别结果组件，否则显示普通文本 -->
          <disease-result v-if="diseaseData" :resultData="diseaseData"
            :imageUrl="images && images.length > 0 ? images[0] : null" @show-full-image="showFullImage" />

          <!-- 图片展示区：只在非病害识别结果时显示 -->
          <div v-if="!diseaseData && images && images.length" class="message-images">
            <div v-for="(image, index) in images" :key="index" class="image-item">
              <img :src="image" alt="图片" @click="showFullImage(image)">
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import DiseaseResult from './DiseaseResult.vue';

export default {
  name: 'ChatMessage',
  components: {
    DiseaseResult
  },
  props: {
    content: {
      type: String,
      default: ''
    },
    isUser: {
      type: Boolean,
      default: false
    },
    // timestamp: {
    //   type: Number,
    //   default: () => Date.now()
    // },
    images: {
      type: Array,
      default: () => []
    },
    diseaseData: {
      type: Object,
      default: null
    },
    cropName: {
      type: String,
      default: '未指定作物'
    }
  },
  data() {
    return {
      displayedContent: '',
      fullContent: '',
      streamInterval: null,
      currentIndex: 0
    }
  },
  computed: {
    formattedHTML() {
      return this.processContent(this.content);
    }
  },
  watch: {
    content: {
      immediate: true,
      handler(newContent) {
        // 当内容变化时重新开始流式显示
        if (this.streamInterval) {
          clearInterval(this.streamInterval);
        }
        
        if (this.isUser) {
          // 用户消息直接显示，不需要流式效果
          this.displayedContent = this.processContent(newContent);
        } else {
          // AI消息需要流式显示
          this.fullContent = this.processContent(newContent);
          this.displayedContent = '';
          this.currentIndex = 0;
          this.startStreaming();
        }
      }
    }
  },
  methods: {
    startStreaming() {
      // 提取纯文本内容
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.fullContent;
      const textContent = tempDiv.textContent || tempDiv.innerText;
      
      // 创建一个HTML解析器，保留HTML标记但流式显示文本
      const streamText = () => {
        if (this.currentIndex < textContent.length) {
          // 每次添加两个字符
          const charsToAdd = Math.min(2, textContent.length - this.currentIndex);
          this.currentIndex += charsToAdd;
          
          // 重新处理显示内容
          const partialText = textContent.substring(0, this.currentIndex);
          // 将部分文本再次转换为Markdown后的HTML
          this.displayedContent = this.processContent(partialText);
        } else {
          // 完成流式显示，显示完整内容
          this.displayedContent = this.fullContent;
          clearInterval(this.streamInterval);
        }
      };
      
      this.streamInterval = setInterval(streamText, 50);
    },
    processContent(content) {
      if (!content) return '';

      // 配置Markdown解析选项
      marked.setOptions({
        breaks: true,     // 自动转换换行符为<br>
        gfm: true,        // 支持GitHub风格的Markdown
        headerIds: false  // 禁用自动生成header id
      });

      // 转换Markdown为HTML并净化
      const rawHtml = marked(content);
      const cleanHtml = DOMPurify.sanitize(rawHtml, {
        ALLOWED_TAGS: [
          'p', 'strong', 'em', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'a', 'br', 'code', 'pre', 'blockquote', 'img', 'hr', 'div', 'button'
        ],
        ALLOWED_ATTR: ['href', 'src', 'alt']
      });

      return cleanHtml;
    },
    showFullImage(image) {
      // 点击图片时触发，可以实现全屏预览
      this.$emit('show-full-image', image);
    }
  },
  beforeDestroy() {
    // 组件销毁前清除定时器
    if (this.streamInterval) {
      clearInterval(this.streamInterval);
    }
  }
}
</script>

<style scoped>

/* 新增列表样式 */
.message-body ::v-deep ol,
.message-body ::v-deep ul {
  padding-left: 24px;
  margin: 8px 0;
  text-align: left;
}

.message-body ::v-deep li {
  margin: 4px 0;
  line-height: 1.6;
    text-align: left;
}

.message-body ::v-deep p {
  margin: 8px 0;
    text-align: left;
}

.message-body ::v-deep strong {
  font-weight: 600;
  color: #2c3e50;
}
.message-container {
  display: flex;
  width: 879px;
  margin: 0 auto 20px;
}

.user-message {
  flex-direction: row-reverse;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #f0f0f0;
  margin: 0 12px;
  flex-shrink: 0;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 空白头像占位样式 */
.empty-avatar-space {
  width: 88px;
  margin: 0 12px;
  flex-shrink: 0;
}

.message-content {
  flex: 1;
  max-width: 80%;
}

.message-header {
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

.sender-name {
  font-weight: bold;
  margin-right: 8px;
}

.message-time {
  font-size: 12px;
  color: #999;
}

.message-body {
  text-align: left !important;
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 8px;
  word-break: break-word;
  line-height: 1.6;
}

/* 确保所有文本元素继承左对齐 */
.message-body ::v-deep * {
  text-align: inherit !important;
}

/* 覆盖可能存在的居中样式 */
.message-container {
  justify-content: flex-start !important;
}

.user-message .message-body {
  background-color: #ffffff;
}

.message-images {
  display: flex;
  flex-wrap: wrap;
  margin-top: 8px;
  gap: 8px;
}

.image-item {
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}


</style>